import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchProducts, fetchProductStats, createProduct, updateProduct, deleteProduct } from "../store/actions/productActions";
import { fetchStores } from "../store/actions/storeActions";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import {
  Plus,
  Search,
  Filter,
  Upload,
  Download,
  Edit,
  MoreVertical,
  Package,
  AlertCircle,
  TagsIcon,
  Store,
  ArrowRight,
  Trash2,
} from "lucide-react";
import { useToast } from "../hooks/useToast";
import { formatCurrency } from "../utils/formatCurrency";
import * as ratesService from "../services/ratesService";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const statKeys = [
  { nameKey: "products.totalProducts", key: "totalProducts", change: "", icon: Package },
  { nameKey: "products.lowStock", key: "lowStockItems", change: "", icon: AlertCircle },
  { nameKey: "products.outOfStock", key: "outOfStockItems", change: "", icon: AlertCircle },
  { nameKey: "products.inventoryValue", key: "inventoryValue", format: "currency", change: "", icon: Package },
  { nameKey: "products.onSale", key: "onSaleCount", change: "", icon: TagsIcon },
  { nameKey: "products.categories", key: "categories", change: "", icon: TagsIcon },
  { nameKey: "products.stores", key: "activeStores", change: "", icon: Store },
];


const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const Sub = styled.p`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.text.body};
`;

const StatGrid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const StatCard = styled(Card)`
  padding: 20px;
`;

const StatTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.body};
`;

const StatValue = styled.div`
  margin-top: 6px;
  font-size: 26px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const IconBubble = styled.div`
  border-radius: 9999px;
  padding: 10px;
  background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
  }
`;

const OverviewTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 24px;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 24px;
`;

const ModalCard = styled(Card)`
  width: 100%;
  max-width: 480px;
  padding: 24px;
  max-height: 90vh;
  overflow-y: auto;
`;

export default function Products() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  function statusText(status) {
    if (status === "in_stock") return t("products.inStock");
    if (status === "low_stock") return t("products.lowStock");
    if (status === "out_of_stock") return t("products.outOfStock");
    return status;
  }
  const { products = [], stats: productStats, loading } = useSelector((state) => state.product);
  const { stores = [] } = useSelector((state) => state.store);
  const [tab, setTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStoreId, setFilterStoreId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [newProductOpen, setNewProductOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [menuProductId, setMenuProductId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const user = useSelector((state) => state.auth?.user);
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [rates, setRates] = useState(null);

  useEffect(() => {
    ratesService.getRates().then((r) => setRates(r?.rates || null)).catch(() => setRates(null));
  }, []);

  useEffect(() => {
    dispatch(fetchStores());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchProducts({ storeId: filterStoreId || undefined, status: filterStatus || undefined }));
    dispatch(fetchProductStats());
  }, [dispatch, filterStoreId, filterStatus]);

  useEffect(() => {
    if (location.state?.editProduct) {
      setEditProduct(location.state.editProduct);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.editProduct, navigate, location.pathname]);

  const baseCurrency = "USD";
  const stats = statKeys.map((s) => {
    const raw = productStats != null && s.key in productStats ? productStats[s.key] : null;
    let value = loading && productStats == null ? "…" : raw == null ? "—" : String(raw);
    if (s.format === "currency" && typeof raw === "number") {
      const converted = rates ? ratesService.convert(raw, baseCurrency, currency, rates) : raw;
      value = formatCurrency(converted, currency);
    }
    return { ...s, value };
  });

  const filteredProducts = products.filter((p) =>
    `${p.name || ""} ${p.sku || ""}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  async function handleCreateProduct(e) {
    e.preventDefault();
    const form = e.target;
    const storeId = form.storeId?.value;
    const name = (form.name?.value || "").trim();
    const price = parseFloat(form.price?.value) || 0;
    const salePrice = form.salePrice?.value ? parseFloat(form.salePrice.value) : undefined;
    const sku = (form.sku?.value || "").trim();
    const stock = parseInt(form.stock?.value, 10) || 0;
    const category = (form.category?.value || "").trim();
    const productType = (form.productType?.value || "").trim();
    const vendor = (form.vendor?.value || "").trim();
    const description = (form.description?.value || "").trim();
    const shortDescription = (form.shortDescription?.value || "").trim();
    const weight = form.weight?.value ? parseFloat(form.weight.value) : undefined;
    const dimensions = (form.dimensions?.value || "").trim();
    const tags = (form.tags?.value || "").trim();
    const image = (form.image?.value || "").trim();
    if (!name || !storeId) { toast({ title: t("products.nameAndStoreRequired"), variant: "destructive" }); return; }
    setSubmitting(true);
    const result = await dispatch(createProduct({
      storeId, name, price, salePrice, sku, stock, category, productType, vendor,
      description, shortDescription, weight, dimensions, tags, image,
    }));
    setSubmitting(false);
    if (result?.success) { setNewProductOpen(false); dispatch(fetchProducts()); dispatch(fetchProductStats()); toast({ title: t("products.productCreated") }); }
    else { toast({ title: t("products.failed"), description: result?.error, variant: "destructive" }); }
  }

  async function handleUpdateProduct(e) {
    e.preventDefault();
    if (!editProduct) return;
    const form = e.target;
    const name = (form.name?.value || "").trim();
    const price = parseFloat(form.price?.value) || 0;
    const salePrice = form.salePrice?.value ? parseFloat(form.salePrice.value) : undefined;
    const sku = (form.sku?.value || "").trim();
    const stock = parseInt(form.stock?.value, 10) || 0;
    const category = (form.category?.value || "").trim();
    const productType = (form.productType?.value || "").trim();
    const vendor = (form.vendor?.value || "").trim();
    const description = (form.description?.value || "").trim();
    const shortDescription = (form.shortDescription?.value || "").trim();
    const weight = form.weight?.value ? parseFloat(form.weight.value) : undefined;
    const dimensions = (form.dimensions?.value || "").trim();
    const tags = (form.tags?.value || "").trim();
    const status = form.status?.value || "in_stock";
    const image = (form.image?.value || "").trim();
    if (!name) { toast({ title: t("products.nameRequiredError"), variant: "destructive" }); return; }
    setSubmitting(true);
    await dispatch(updateProduct(editProduct._id, {
      name, price, salePrice, sku, stock, category, productType, vendor,
      description, shortDescription, weight, dimensions, tags, status, image,
    }));
    setSubmitting(false);
    setEditProduct(null);
    dispatch(fetchProducts());
    dispatch(fetchProductStats());
    toast({ title: t("products.productUpdated") });
  }

  async function handleDeleteProductConfirm() {
    if (!deleteProductId) return;
    await dispatch(deleteProduct(deleteProductId));
    setDeleteProductId(null);
    dispatch(fetchProducts());
    dispatch(fetchProductStats());
    toast({ title: t("products.productDeleted") });
  }

  return (
    <div className="animate-fadeIn">
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between" }}>
        <div>
          <Title>{t("products.title")}</Title>
          <Sub>{t("products.subtitle")}</Sub>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>
          <Button $variant="outline" onClick={() => navigate("/get-started", { state: { openProductWizard: true } })}>
            <Upload size={16} />
            {t("products.import")}
          </Button>
          <Button onClick={() => setNewProductOpen(true)}>
            <Plus size={16} />
            {t("products.newProduct")}
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("products.overview")}</TabsTrigger>
          <TabsTrigger value="products">{t("products.productsTab")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div style={{ marginTop: 24 }} className="space-y-6">
            <StatGrid>
              {stats.map((s) => {
                const Icon = s.icon;
                return (
                  <StatCard key={s.nameKey} className="glass-card">
                    <StatTop>
                      <div>
                        <StatName>{t(s.nameKey)}</StatName>
                        <StatValue>{loading && productStats == null ? "…" : s.value}</StatValue>
                      </div>
                      <IconBubble>
                        <Icon size={20} />
                      </IconBubble>
                    </StatTop>
                    {s.change ? (
                      <div style={{ marginTop: 10 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#16a34a" }}>{s.change}</span>
                        <span style={{ fontSize: 13, color: "#6b7280" }}> {t("products.fromLastMonth")}</span>
                      </div>
                    ) : null}
                  </StatCard>
                );
              })}
            </StatGrid>

            <div style={{ marginTop: 32 }}>
            <TwoCol>
              <Card style={{ padding: 20 }}>
                <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>{t("products.productsByStore")}</h2>
                <div style={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={productStats?.productsByStore?.length ? productStats.productsByStore : [{ name: t("common.noData"), products: 0 }]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="products" fill="#8b5cf6" name={t("products.productsTab")} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card style={{ padding: 20 }}>
                <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>{t("products.quickActions")}</h2>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <Button onClick={() => { setTab("products"); }} $variant="outline">
                    {t("products.viewAllProducts")}
                    <ArrowRight size={16} />
                  </Button>
                  <Button $variant="outline" onClick={() => navigate("/stores")}>
                    <Store size={16} />
                    {t("products.manageStores")}
                  </Button>
                  <Button $variant="outline" onClick={() => navigate("/get-started", { state: { openProductWizard: true } })}>
                    <Download size={16} />
                    {t("products.importProducts")}
                  </Button>
                </div>
              </Card>
            </TwoCol>
            </div>

            {productStats?.topProductsByQuantity?.length > 0 && (
              <Card style={{ padding: 20, marginTop: 24 }}>
                <h2 style={{ margin: 0, marginBottom: 4, fontSize: 18, fontWeight: 700 }}>{t("products.topPerforming")}</h2>
                <p style={{ margin: 0, marginBottom: 16, fontSize: 13, color: "#6b7280" }}>{t("products.byQuantitySold")}</p>
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {productStats.topProductsByQuantity.slice(0, 8).map((p, idx) => (
                    <li key={p.productId + (p.storeId?.toString?.() || p.storeId) || idx} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      {p._id ? (
                        <button type="button" onClick={() => navigate(`/products/${p._id}`)} style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", fontWeight: 500 }}>
                          {p.name}
                        </button>
                      ) : (
                        <span style={{ fontWeight: 500 }}>{p.name}</span>
                      )}
                      <span style={{ color: "#6b7280", fontSize: 14 }}>
                        <strong>{p.quantity}</strong> sold · ${Number(p.revenue || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <OverviewTwoCol>
              <Card style={{ padding: 20 }}>
                <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>{t("products.lowStockProducts")}</h2>
                {(productStats?.lowStockProducts?.length && productStats.lowStockProducts.length > 0) ? (
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {productStats.lowStockProducts.slice(0, 8).map((p) => (
                      <li key={p._id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                        <button type="button" onClick={() => navigate(`/products/${p._id}`)} style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontWeight: 500 }}>{p.name || p.sku || "—"}</span>
                          <span style={{ color: "#b45309", fontWeight: 600 }}>{t("products.stockLabel")} {p.stock ?? 0}</span>
                        </button>
                        {p.storeId?.name && <span style={{ fontSize: 12, color: "#6b7280" }}>{p.storeId.name}</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("products.noLowStock")}</p>
                )}
                {productStats?.lowStockProducts?.length > 8 && (
                  <Button $variant="ghost" $size="sm" style={{ marginTop: 8 }} onClick={() => setTab("products")}>{t("products.viewAll")}</Button>
                )}
              </Card>
              <Card style={{ padding: 20 }}>
                <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>{t("products.recentlyAdded")}</h2>
                {(productStats?.recentlyAdded?.length && productStats.recentlyAdded.length > 0) ? (
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {productStats.recentlyAdded.slice(0, 8).map((p) => (
                      <li key={p._id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                        <button type="button" onClick={() => navigate(`/products/${p._id}`)} style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", width: "100%", fontWeight: 500 }}>
                          {p.name || p.sku || "—"}
                        </button>
                        {p.storeId?.name && <span style={{ fontSize: 12, color: "#6b7280", display: "block" }}>{p.storeId.name}</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("products.noProductsYet")}</p>
                )}
                {productStats?.recentlyAdded?.length > 8 && (
                  <Button $variant="ghost" $size="sm" style={{ marginTop: 8 }} onClick={() => setTab("products")}>{t("products.viewAll")}</Button>
                )}
              </Card>
            </OverviewTwoCol>
          </div>
        </TabsContent>

        <TabsContent value="products">
          <div style={{ marginTop: 24 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ maxWidth: 420 }}>
                <div style={{ position: "relative" }}>
                  <Search size={16} color="#6b7280" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
                  <Input placeholder={t("products.searchProducts")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ paddingLeft: 34 }} />
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <select
                  value={filterStoreId}
                  onChange={(e) => setFilterStoreId(e.target.value)}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}
                >
                  <option value="">{t("products.allStores")}</option>
                  {stores.map((s) => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}
                >
                  <option value="">{t("products.allStatuses")}</option>
                  <option value="in_stock">{t("products.inStock")}</option>
                  <option value="low_stock">{t("products.lowStock")}</option>
                  <option value="out_of_stock">{t("products.outOfStock")}</option>
                </select>
                <Button $variant="outline" $size="sm"><Download size={14} /> {t("products.export")}</Button>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("products.product")}</TableHead>
                    <TableHead>{t("products.store")}</TableHead>
                    <TableHead>{t("products.category")}</TableHead>
                    <TableHead>{t("products.sku")}</TableHead>
                    <TableHead>{t("products.stock")}</TableHead>
                    <TableHead>{t("products.price")}</TableHead>
                    <TableHead style={{ textAlign: "right" }}>{t("products.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow key={product._id}>
                      <TableCell>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <img
                            src={product.image || "https://via.placeholder.com/40"}
                            alt={product.name || ""}
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 12,
                              objectFit: "cover",
                            }}
                          />
                          <div>
                            <div style={{ fontWeight: 600 }}>{product.name}</div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                              {statusText(product.status)}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{product.storeId?.name || "—"}</TableCell>
                      <TableCell>{product.category || "—"}</TableCell>
                      <TableCell>{product.sku || "—"}</TableCell>
                      <TableCell>{product.stock ?? "—"}</TableCell>
                      <TableCell>{typeof product.price === "number" ? formatCurrency(rates && (product.currency || "USD") !== currency ? ratesService.convert(product.price, product.currency || "USD", currency, rates) : product.price, currency) : product.price ?? "—"}</TableCell>
                      <TableCell style={{ textAlign: "right", position: "relative" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                          <Button $variant="outline" $size="sm" onClick={() => navigate(`/products/${product._id}`)}>
                            <Edit size={14} /> {t("products.details")}
                          </Button>
                          <Button $variant="ghost" $size="icon" onClick={() => setMenuProductId(menuProductId === product._id ? null : product._id)} aria-label="Actions">
                            <MoreVertical size={16} />
                          </Button>
                          {menuProductId === product._id && (
                            <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 10, minWidth: 140, padding: 4 }}>
                              <button type="button" style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", background: "none", cursor: "pointer", fontSize: 13 }} onClick={() => { setEditProduct(product); setMenuProductId(null); }}><Edit size={14} style={{ verticalAlign: "middle", marginRight: 6 }} /> {t("products.edit")}</button>
                              <button type="button" style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", background: "none", cursor: "pointer", fontSize: 13, color: "#dc2626" }} onClick={() => { setDeleteProductId(product._id); setMenuProductId(null); }}><Trash2 size={14} style={{ verticalAlign: "middle", marginRight: 6 }} /> {t("products.delete")}</button>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {newProductOpen && (
        <ModalOverlay onClick={() => !submitting && setNewProductOpen(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700 }}>{t("products.newProductTitle")}</h2>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#6b7280" }}>{t("products.newProductSub")}</p>
            <form onSubmit={handleCreateProduct}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.storeRequired")}</label>
                <select name="storeId" required style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
                  <option value="">{t("products.selectStore")}</option>
                  {stores.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.nameRequired")}</label>
                <Input name="name" placeholder={t("products.productName")} required />
              </div>
              <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.priceRequired")}</label>
                  <Input name="price" type="number" step="0.01" min="0" placeholder="0" defaultValue="0" required />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.salePrice")}</label>
                  <Input name="salePrice" type="number" step="0.01" min="0" placeholder={t("products.optional")} />
                </div>
              </div>
              <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.stock")}</label>
                  <Input name="stock" type="number" min="0" placeholder="0" defaultValue="0" />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.sku")}</label>
                  <Input name="sku" placeholder={t("products.optional")} />
                </div>
              </div>
              <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.category")}</label>
                  <Input name="category" placeholder={t("products.optional")} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.productType")}</label>
                  <Input name="productType" placeholder={t("products.optional")} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.vendor")}</label>
                <Input name="vendor" placeholder={t("products.optional")} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.imageUrl")}</label>
                <Input name="image" placeholder="https://..." />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.shortDescription")}</label>
                <Input name="shortDescription" placeholder={t("products.optional")} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.description")}</label>
                <textarea name="description" placeholder={t("products.optional")} rows={3} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }} />
              </div>
              <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.weightKg")}</label>
                  <Input name="weight" type="number" step="0.01" min="0" placeholder={t("products.optional")} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.dimensions")}</label>
                  <Input name="dimensions" placeholder={t("products.dimensionsPlaceholder")} />
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.tags")}</label>
                <Input name="tags" placeholder={t("products.tagsPlaceholder")} />
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
                <Button type="button" $variant="outline" onClick={() => setNewProductOpen(false)} disabled={submitting}>{t("common.cancel")}</Button>
                <Button type="submit" disabled={submitting}>{submitting ? t("products.creating") : t("products.create")}</Button>
              </div>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}

      {editProduct && (
        <ModalOverlay onClick={() => !submitting && setEditProduct(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700 }}>{t("products.editProductTitle")}</h2>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#6b7280" }}>{t("products.editProductSub")}</p>
            <form onSubmit={handleUpdateProduct}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.nameRequired")}</label>
                <Input name="name" defaultValue={editProduct.name} required />
              </div>
              <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.priceRequired")}</label>
                  <Input name="price" type="number" step="0.01" min="0" defaultValue={editProduct.price} required />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.salePrice")}</label>
                  <Input name="salePrice" type="number" step="0.01" min="0" defaultValue={editProduct.salePrice ?? ""} placeholder={t("products.optional")} />
                </div>
              </div>
              <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.stock")}</label>
                  <Input name="stock" type="number" min="0" defaultValue={editProduct.stock ?? 0} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Status</label>
                  <select name="status" defaultValue={editProduct.status || "in_stock"} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
                    <option value="in_stock">{t("products.inStock")}</option>
                    <option value="low_stock">{t("products.lowStock")}</option>
                    <option value="out_of_stock">{t("products.outOfStock")}</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.sku")}</label>
                <Input name="sku" defaultValue={editProduct.sku || ""} />
              </div>
              <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.category")}</label>
                  <Input name="category" defaultValue={editProduct.category || ""} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.productType")}</label>
                  <Input name="productType" defaultValue={editProduct.productType || ""} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.vendor")}</label>
                <Input name="vendor" defaultValue={editProduct.vendor || ""} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.imageUrl")}</label>
                <Input name="image" defaultValue={editProduct.image || ""} placeholder="https://..." />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.shortDescription")}</label>
                <Input name="shortDescription" defaultValue={editProduct.shortDescription || ""} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.description")}</label>
                <textarea name="description" defaultValue={editProduct.description || ""} rows={3} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }} />
              </div>
              <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.weightKg")}</label>
                  <Input name="weight" type="number" step="0.01" min="0" defaultValue={editProduct.weight ?? ""} placeholder={t("products.optional")} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.dimensions")}</label>
                  <Input name="dimensions" defaultValue={editProduct.dimensions || ""} placeholder={t("products.dimensionsPlaceholder")} />
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("products.tags")}</label>
                <Input name="tags" defaultValue={editProduct.tags || ""} placeholder={t("products.tagsPlaceholder")} />
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
                <Button type="button" $variant="outline" onClick={() => setEditProduct(null)} disabled={submitting}>{t("common.cancel")}</Button>
                <Button type="submit" disabled={submitting}>{submitting ? t("products.saving") : t("common.save")}</Button>
              </div>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}

      {deleteProductId && (
        <ModalOverlay onClick={() => setDeleteProductId(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700 }}>{t("products.deleteProductTitle")}</h2>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#6b7280" }}>{t("products.deleteProductSub")}</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
              <Button $variant="outline" onClick={() => setDeleteProductId(null)}>{t("common.cancel")}</Button>
              <Button onClick={handleDeleteProductConfirm} style={{ background: "#dc2626" }}>{t("products.delete")}</Button>
            </div>
          </ModalCard>
        </ModalOverlay>
      )}
    </div>
  );
}

